import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, BookOpen, Briefcase, FileText, HelpCircle, ChevronRight, MessageSquare, Landmark, CalendarDays, Database, Megaphone } from 'lucide-react';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import MarkdownRenderer from '../help/components/MarkdownRenderer';
import FeedbackFormModal from '../components/FeedbackFormModal';
import {
  helpArticles,
  categories,
  getArticleBySlug,
  getArticlesByCategory,
  searchArticles,
  HelpArticle,
  HelpCategory,
  CategoryInfo
} from '../help/content';

// Category icons mapping
const categoryIcons: Record<HelpCategory, React.ReactNode> = {
  'getting-started': <BookOpen className="w-5 h-5" />,
  'workspaces': <Briefcase className="w-5 h-5" />,
  'legislation': <Landmark className="w-5 h-5" />,
  'calendar': <CalendarDays className="w-5 h-5" />,
  'outreach': <Megaphone className="w-5 h-5" />,
  'reports': <FileText className="w-5 h-5" />,
  'directories': <Database className="w-5 h-5" />,
  'general': <HelpCircle className="w-5 h-5" />
};

// Article Card Component
const ArticleCard: React.FC<{
  article: HelpArticle;
  onClick: () => void;
}> = ({ article, onClick }) => {
  const category = categories.find(c => c.id === article.category);

  return (
    <button
      onClick={onClick}
      className="card p-5 text-left hover:shadow-md transition-shadow w-full"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
          {categoryIcons[article.category]}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary mb-1">
            {article.title}
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2">
            {article.description}
          </p>
          {category && (
            <span className="inline-block mt-2 text-xs px-2 py-1 rounded-full bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary">
              {category.name}
            </span>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
      </div>
    </button>
  );
};

// Category Card Component
const CategoryCard: React.FC<{
  category: CategoryInfo;
  articleCount: number;
  isActive: boolean;
  onClick: () => void;
}> = ({ category, articleCount, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-4 rounded-lg transition-colors ${
      isActive
        ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500'
        : 'bg-white dark:bg-background-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary border-2 border-transparent'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
        isActive
          ? 'bg-primary-600 text-white'
          : 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
      }`}>
        {categoryIcons[category.id]}
      </div>
      <div className="flex-1">
        <h3 className={`text-sm font-medium ${
          isActive
            ? 'text-primary-700 dark:text-primary-300'
            : 'text-text-primary dark:text-text-dark-primary'
        }`}>
          {category.name}
        </h3>
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
          {articleCount} article{articleCount !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  </button>
);

// Main Help Center Page
const HelpCenterPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageMetadata } = usePageMetadata();

  // State
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | 'all'>(
    (searchParams.get('category') as HelpCategory) || 'all'
  );
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  // Set page metadata
  useEffect(() => {
    setPageMetadata({
      title: 'Help Center',
      description: 'Find answers and learn how to use Capitol Commons'
    });
    return () => setPageMetadata(null);
  }, [setPageMetadata]);

  // Load article from URL if present
  useEffect(() => {
    const articleSlug = searchParams.get('article');
    if (articleSlug) {
      const article = getArticleBySlug(articleSlug);
      if (article) {
        setSelectedArticle(article);
      }
    }
  }, [searchParams]);

  // Filter articles based on search and category
  const filteredArticles = useMemo(() => {
    let results = helpArticles;

    if (searchQuery.trim()) {
      results = searchArticles(searchQuery);
    }

    if (selectedCategory !== 'all') {
      results = results.filter(article => article.category === selectedCategory);
    }

    return results;
  }, [searchQuery, selectedCategory]);

  // Get article counts by category
  const articleCountByCategory = useMemo(() => {
    const counts: Record<HelpCategory, number> = {
      'getting-started': 0,
      'workspaces': 0,
      'legislation': 0,
      'calendar': 0,
      'outreach': 0,
      'reports': 0,
      'directories': 0,
      'general': 0
    };

    helpArticles.forEach(article => {
      counts[article.category]++;
    });

    return counts;
  }, []);

  // Handle article selection
  const handleSelectArticle = (article: HelpArticle) => {
    setSelectedArticle(article);
    setSearchParams({ article: article.slug });
  };

  // Handle category change
  const handleCategoryChange = (category: HelpCategory | 'all') => {
    setSelectedCategory(category);
    setSelectedArticle(null);
    const newParams = new URLSearchParams();
    if (searchQuery) newParams.set('q', searchQuery);
    if (category !== 'all') newParams.set('category', category);
    setSearchParams(newParams);
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSelectedArticle(null);
    const newParams = new URLSearchParams();
    if (query) newParams.set('q', query);
    if (selectedCategory !== 'all') newParams.set('category', selectedCategory);
    setSearchParams(newParams);
  };

  // Article View
  if (selectedArticle) {
    return (
      <div className="w-full max-w-4xl">
        {/* Feedback button in upper right */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors shadow-sm hover:shadow-md text-sm"
          >
            <MessageSquare className="w-4 h-4" />
            Contact Support
          </button>
        </div>
        
        {/* Article content */}
        <div className="card p-6 lg:p-8">
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 mb-4">
              {categoryIcons[selectedArticle.category]}
              {categories.find(c => c.id === selectedArticle.category)?.name}
            </span>
          </div>
          <MarkdownRenderer content={selectedArticle.content} />
          {selectedArticle.updated && (
            <p className="mt-8 pt-4 border-t border-border dark:border-border-dark text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Last updated {new Date(selectedArticle.updated + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>

        {/* Related articles */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
            Related Articles
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getArticlesByCategory(selectedArticle.category)
              .filter(a => a.slug !== selectedArticle.slug)
              .slice(0, 2)
              .map(article => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  onClick={() => handleSelectArticle(article)}
                />
              ))}
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="w-full">
      {/* Hero Section */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-3">
          <div className="text-center lg:text-left flex-1">
            <h1 className="text-3xl font-bold text-text-primary dark:text-text-dark-primary mb-3">
              How can we help you?
            </h1>
            <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
              Search our knowledge base or browse by category
            </p>
          </div>
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors shadow-sm hover:shadow-md text-sm whitespace-nowrap"
          >
            <MessageSquare className="w-4 h-4" />
            Contact Support
          </button>
        </div>

        {/* Search bar */}
        <div className="max-w-2xl mx-auto lg:mx-0 relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
          <input
            type="text"
            placeholder="Search for help articles..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="input pl-12 py-3 text-base"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar - Categories */}
        <aside className="lg:w-64 flex-shrink-0">
          <h2 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary uppercase tracking-wide mb-4">
            Categories
          </h2>
          <div className="space-y-2">
            {/* All articles option */}
            <button
              onClick={() => handleCategoryChange('all')}
              className={`w-full text-left p-3 rounded-lg transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                  : 'hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary'
              }`}
            >
              All Articles ({helpArticles.length})
            </button>

            {/* Category buttons */}
            {categories
              .sort((a, b) => a.order - b.order)
              .map(category => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  articleCount={articleCountByCategory[category.id]}
                  isActive={selectedCategory === category.id}
                  onClick={() => handleCategoryChange(category.id)}
                />
              ))}
          </div>
        </aside>

        {/* Main content - Articles */}
        <main className="flex-1">
          {/* Results header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {searchQuery
                ? `Search results for "${searchQuery}"`
                : selectedCategory === 'all'
                ? 'All Articles'
                : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
              {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Articles list */}
          {filteredArticles.length > 0 ? (
            <div className="space-y-4">
              {filteredArticles.map(article => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  onClick={() => handleSelectArticle(article)}
                />
              ))}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <HelpCircle className="w-12 h-12 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
                No articles found
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
                {searchQuery
                  ? `We couldn't find any articles matching "${searchQuery}"`
                  : 'No articles in this category yet'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => handleSearch('')}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Quick help section */}
      <div className="mt-12 border-t border-border-light dark:border-border-dark pt-8">
        <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4 text-center">
          Need more help?
        </h2>
        <p className="text-center text-text-secondary dark:text-text-dark-secondary">
          Look for the <HelpCircle className="w-4 h-4 inline-block mx-1" /> help icons throughout the application for contextual guidance.
        </p>
      </div>

      {/* Feedback Modal */}
      <FeedbackFormModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </div>
  );
};

export default HelpCenterPage;

